import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { capacitorPlatform, isCapacitorNative } from '../utils/isCapacitorNative';
import {
  DEFAULT_APPLE_REDIRECT_URI,
  resolveAppleRedirectUri,
  type AppleFirstSignInUser,
} from './appleSignIn';

export type NativeAppleSignInResult = {
  idToken: string;
  user?: AppleFirstSignInUser;
};

/** App ID used for native Sign in with Apple (not the web Services ID). */
export const DEFAULT_APPLE_NATIVE_CLIENT_ID = 'ai.offizz.app';

export function resolveAppleNativeClientId(): string {
  return (
    process.env.REACT_APP_APPLE_NATIVE_CLIENT_ID?.trim() ||
    process.env.CAPACITOR_APP_ID?.trim() ||
    DEFAULT_APPLE_NATIVE_CLIENT_ID
  );
}

/** True inside the Capacitor iOS shell — uses the system Apple ID sheet, not the web login page. */
export function isNativeAppleSignInAvailable(): boolean {
  return isCapacitorNative() && capacitorPlatform() === 'ios';
}

function errorBlob(error: unknown): string {
  if (error instanceof Error) {
    const extra = error as Error & { code?: unknown };
    return [error.message, extra.code != null ? String(extra.code) : '']
      .filter((part) => part.trim().length > 0)
      .join(' ');
  }
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error ?? '');
}

export function isNativeAppleSignInCancelled(error: unknown): boolean {
  const blob = errorBlob(error);
  return /cancel|dismiss|closed|user.*abort|1001|ERR_CANCELED/i.test(blob);
}

/** Plugin missing / not linked in this binary (do not fall back to web SIWA on iOS — App Store 4.0). */
export function isNativeApplePluginMissing(error: unknown): boolean {
  const blob = errorBlob(error);
  return /not implemented|UNIMPLEMENTED|"code":"UNIMPLEMENTED"|plugin is not implemented|is not implemented on ios|Native Apple Sign-In plugin/i.test(
    blob,
  );
}

/**
 * Native Sign in with Apple (iOS Capacitor).
 * Requires `@capacitor-community/apple-sign-in` in the iOS binary + Sign in with Apple entitlement.
 */
export async function requestNativeAppleIdToken(): Promise<NativeAppleSignInResult> {
  if (!isNativeAppleSignInAvailable()) {
    throw new Error('Native Apple Sign-In is only available in the iOS app');
  }

  const clientId = resolveAppleNativeClientId();
  let redirectURI = DEFAULT_APPLE_REDIRECT_URI;
  try {
    redirectURI = resolveAppleRedirectUri();
  } catch {
    /* keep default */
  }

  const result = await SignInWithApple.authorize({
    clientId,
    redirectURI,
    scopes: 'email name',
  });

  const identityToken = result.response?.identityToken?.trim() ?? '';
  if (!identityToken) {
    throw new Error('Apple Sign-In did not return an identity token');
  }

  const email = result.response?.email?.trim() || undefined;
  const firstName = result.response?.givenName?.trim() || undefined;
  const lastName = result.response?.familyName?.trim() || undefined;
  const user: AppleFirstSignInUser | undefined =
    email || firstName || lastName
      ? {
          ...(email ? { email } : {}),
          ...(firstName || lastName
            ? {
                name: {
                  ...(firstName ? { firstName } : {}),
                  ...(lastName ? { lastName } : {}),
                },
              }
            : {}),
        }
      : undefined;

  return { idToken: identityToken, user };
}
